import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { apiFetch } from '../api';
import Modal from '../components/Modal';
import InfoTip from '../components/InfoTip';
import { useConfirm } from '../components/ConfirmDialog';
import {
  Badge, EmptyState, Field, Icon, PageHeader, TableSkeleton,
} from '../components/ui';

/**
 * Admin management — Cognito-backed (2026-05-26 cutover).
 *
 * Two visible UX changes vs. the legacy local-auth page:
 *  - "Create admin" no longer surfaces a one-time password modal. Cognito
 *    emails the temp password directly to the new admin's address.
 *  - "Resend invite" and "Reset password" actions per row, both backend
 *    calls into cognito-idp.
 *
 * Path key flipped from {admin_id INTEGER} to {username STRING} —
 * matches the new /api/admins/{username} routes.
 */
// Cognito's own status strings, in Sentence case. Only FORCE_CHANGE_PASSWORD was
// being translated; everything else fell through raw, so the column mixed
// "Invite pending" with a SHOUTED "CONFIRMED". Unknown values still fall through
// rather than being hidden — a status we have not seen is worth showing verbatim.
const STATUS_LABELS = {
  CONFIRMED: 'Confirmed',
  FORCE_CHANGE_PASSWORD: 'Invite pending',
  UNCONFIRMED: 'Unconfirmed',
  RESET_REQUIRED: 'Reset required',
  ARCHIVED: 'Archived',
  COMPROMISED: 'Compromised',
  UNKNOWN: 'Unknown',
};

// Confirmed is the settled state; anything waiting on the admin to act is amber, and a
// compromised account is red. An unknown status gets the neutral badge.
const STATUS_TONE = {
  CONFIRMED: 'ok',
  FORCE_CHANGE_PASSWORD: 'warn',
  UNCONFIRMED: 'warn',
  RESET_REQUIRED: 'warn',
  COMPROMISED: 'danger',
  ARCHIVED: 'neutral',
  UNKNOWN: 'neutral',
};

const statusLabel = (status) => STATUS_LABELS[status] || status || '—';

// The three levels, least privileged first. One tone per role, used by the table badge
// and by the invite form, so a role reads the same wherever it appears.
const ROLES = [
  { value: 'analyst', label: 'Analyst', tone: 'neutral', desc: 'Read-only access to the Dashboard, Platform and Users pages, with each user\u2019s details and logs but not their coupon code. No other page, and no changes.' },
  { value: 'admin', label: 'Admin', tone: 'accent', desc: 'Manages users, channels, templates and the rest of the console.' },
  { value: 'superadmin', label: 'Superadmin', tone: 'violet', desc: 'Everything, including Admins and Data governance.' },
];

const roleOf = (role) => ROLES.find(r => r.value === role) || { label: role || '—', tone: 'neutral' };

// "19 Jul 2026": a month name cannot be misread as D/M vs M/D. The full timestamp stays
// in the tooltip.
const formatDate = (iso) => (iso
  ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  : null);

// Two letters for the avatar: the initials of a dotted or underscored name, or the first
// two letters of a single word. Decorative, so the avatar is hidden from screen readers.
function initials(name) {
  const s = String(name || '').trim();
  if (!s) return '?';
  const parts = s.split(/[\s._@-]+/).filter(Boolean);
  return parts.length > 1 ? parts[0][0] + parts[1][0] : s.slice(0, 2);
}

function Admins() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState({ username: '', email: '', role: 'admin' });
  const [creating, setCreating] = useState(false);
  // The Cognito call in flight per row ('resend' | 'reset'). Its button shows a spinner and
  // cannot be pressed again, so a slow Cognito does not end in two emails.
  const [busy, setBusy] = useState({});
  const [confirm, confirmDialog] = useConfirm();

  useEffect(() => { fetchAdmins(); }, []);

  const fetchAdmins = async () => {
    try {
      const res = await apiFetch('/api/admins');
      if (res.ok) setAdmins(await res.json());
      else toast.error('Failed to fetch admins');
    } catch { toast.error('Failed to fetch admins'); }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!form.username.trim()) { toast.error('Username is required'); return; }
    if (!form.email.trim()) { toast.error('Email is required (Cognito mails the temp password there)'); return; }
    setCreating(true);
    try {
      const res = await apiFetch('/api/admins', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: form.username.trim(),
          email: form.email.trim(),
          role: form.role,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setShowCreate(false);
        setForm({ username: '', email: '', role: 'admin' });
        toast.success(data.message || `Invite emailed to ${form.email.trim()}`);
        fetchAdmins();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create admin');
      }
    } catch { toast.error('Failed to create admin'); }
    setCreating(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await apiFetch(`/api/admins/${encodeURIComponent(deleteTarget.username)}`, { method: 'DELETE' });
      if (res.ok) { toast.success(`Admin '${deleteTarget.username}' deleted`); fetchAdmins(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed to delete'); }
    } catch { toast.error('Failed to delete admin'); }
    setDeleting(false);
    setDeleteTarget(null);
  };

  const handleResendInvite = async (username) => {
    setBusy(b => ({ ...b, [username]: 'resend' }));
    try {
      const res = await apiFetch(`/api/admins/${encodeURIComponent(username)}/resend-invite`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || `Invite re-emailed to ${username}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to resend invite');
      }
    } catch { toast.error('Failed to resend invite'); }
    setBusy(b => ({ ...b, [username]: null }));
  };

  const handleResetPassword = async (username) => {
    if (!await confirm({
      title: `Email a reset code to ${username}?`,
      body: (
        <p>
          Their current password keeps working until they complete the reset, then
          stops.
        </p>
      ),
      confirmLabel: 'Send reset code',
      tone: 'primary',
    })) return;
    setBusy(b => ({ ...b, [username]: 'reset' }));
    try {
      const res = await apiFetch(`/api/admins/${encodeURIComponent(username)}/reset-password`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || `Reset code emailed to '${username}'`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to send reset code');
      }
    } catch { toast.error('Failed to send reset code'); }
    setBusy(b => ({ ...b, [username]: null }));
  };

  const openCreate = () => setShowCreate(true);
  const closeCreate = () => { if (!creating) setShowCreate(false); };

  return (
    <div>
      <PageHeader
        description="Who can sign in to this console, and at what level."
        actions={(
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            <Icon name="userPlus" /> New admin
          </button>
        )}
      >
        <h1 className="page-title">
          Admins
          {!loading && <Badge tone="neutral" size="lg">{admins.length}</Badge>}
          <InfoTip label="About admin roles" width={340}>
            <p>
              <strong>Analyst</strong> reads the Dashboard, Platform and Users pages, with each
              user&apos;s details and logs but not their coupon code. No other page, and no
              changes.
            </p>
            <p><strong>Admin</strong> runs the console: users, channels, templates, tiers and the rest.</p>
            <p>
              <strong>Superadmin</strong> also manages admins, broadcasts, maintenance and data
              governance.
            </p>
            <p>An invite emails a temporary password, which is changed at first sign-in.</p>
          </InfoTip>
        </h1>
      </PageHeader>

      <div className="card card-flush">
        {loading ? (
          <TableSkeleton rows={4} cols={5} label="Loading admins" />
        ) : admins.length === 0 ? (
          <EmptyState
            icon="admins"
            title="No admins found"
            action={<button type="button" className="btn btn-primary" onClick={openCreate}>Invite an admin</button>}
          >
            Admins sign in to this console. Cognito emails each new one a temporary password.
          </EmptyState>
        ) : (
          <div className="table-container">
            <table className="table table-stack">
              <thead>
                <tr>
                  <th>Admin</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {admins.map(a => {
                  const role = roleOf(a.role);
                  const rowBusy = busy[a.username];
                  return (
                    <tr key={a.username}>
                      <td data-label="Admin">
                        <div className="row gap-3">
                          <span className="avatar" aria-hidden="true">{initials(a.username)}</span>
                          {/* max-width on a <td> is ignored by table layout; on a block inside
                              it, it holds, so a long email truncates instead of widening the
                              column and pushing the actions off screen. */}
                          <div className="min-w-0" style={{ maxWidth: 280 }}>
                            <div className="cell-primary truncate" title={a.username}>{a.username}</div>
                            <span className="cell-sub truncate" title={a.email || undefined}>
                              {a.email || 'No email on file'}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td data-label="Role"><Badge tone={role.tone}>{role.label}</Badge></td>
                      <td data-label="Status">
                        <div className="row-wrap gap-1">
                          <Badge tone={STATUS_TONE[a.status] || 'neutral'} dot>{statusLabel(a.status)}</Badge>
                          {a.enabled === false && <Badge tone="danger">Disabled</Badge>}
                        </div>
                      </td>
                      <td data-label="Created" className="nowrap">
                        {a.created_at
                          ? <span className="num" title={new Date(a.created_at).toLocaleString()}>{formatDate(a.created_at)}</span>
                          : <span className="text-faint">—</span>}
                      </td>
                      <td data-label="Actions">
                        {/* GRID, not the flex .btn-group. The first action's label varies
                            by row — "Resend invite" for a pending invite, "Reset password"
                            for a confirmed one — and in a flex row that put Delete at a
                            different x on every row, so the Delete buttons never lined up
                            down the column.

                            A table column shares ONE width across every row, so a `1fr`
                            first slot resolves to the same width in all of them: both
                            buttons align on both edges, with no hardcoded px to go stale
                            if a label changes. The empty span holds the first slot for a
                            Cognito status that has neither action, which would otherwise
                            drop Delete into the stretchy column. */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr auto',
                          gap: 'var(--sp-2)',
                          alignItems: 'center',
                        }}>
                          {a.status === 'FORCE_CHANGE_PASSWORD' ? (
                            <button type="button" className="btn btn-secondary btn-sm" disabled={!!rowBusy}
                                    onClick={() => handleResendInvite(a.username)}>
                              {rowBusy === 'resend' ? <span className="spinner-sm" aria-hidden="true" /> : <Icon name="mail" />}
                              Resend invite
                            </button>
                          ) : a.status === 'CONFIRMED' ? (
                            <button type="button" className="btn btn-secondary btn-sm" disabled={!!rowBusy}
                                    onClick={() => handleResetPassword(a.username)}>
                              {rowBusy === 'reset' ? <span className="spinner-sm" aria-hidden="true" /> : <Icon name="key" />}
                              Reset password
                            </button>
                          ) : <span />}
                          <button type="button" className="btn btn-danger-soft btn-sm btn-icon"
                                  aria-label={`Delete admin ${a.username}`} title="Delete admin"
                                  onClick={() => setDeleteTarget(a)}>
                            <Icon name="trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={showCreate} onClose={closeCreate} title="Invite an admin"
             description="Cognito emails them a temporary password, which they change at first sign-in.">
        {/* A form so Enter in either field sends the invite. noValidate keeps the checks
            where they were, in the toasts below, rather than adding a browser bubble. */}
        <form noValidate onSubmit={(e) => { e.preventDefault(); if (!creating) handleCreate(); }}>
          <div className="form-row gap-4">
            <Field label="Username" htmlFor="admin-username" required className="mb-0">
              <input
                id="admin-username"
                className="form-input"
                value={form.username}
                onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                autoComplete="off"
                spellCheck={false}
                autoFocus
              />
            </Field>
            <Field label="Email" htmlFor="admin-email" required className="mb-0"
                   hint="Where the temporary password is sent.">
              <input
                id="admin-email"
                className="form-input"
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="user@example.com"
                autoComplete="off"
              />
            </Field>
          </div>

          <div className="mt-5">
            <div className="form-label" id="admin-role-label">Role</div>
            <div role="radiogroup" aria-labelledby="admin-role-label" className="stack gap-2">
              {ROLES.map(r => (
                <label key={r.value} className="check-row card-inset">
                  <input type="radio" name="role" value={r.value} checked={form.role === r.value}
                    onChange={() => setForm(f => ({ ...f, role: r.value }))} />
                  <span className="stack gap-1 items-start">
                    <Badge tone={r.tone}>{r.label}</Badge>
                    <span className="check-hint">{r.desc}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={closeCreate} disabled={creating}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={creating}>
              {creating ? <><span className="spinner-sm" /> Sending invite…</> : <><Icon name="send" /> Send invite</>}
            </button>
          </div>
        </form>
      </Modal>

      {/* Kept as its own Modal rather than moved to useConfirm, because the row
          it acts on is already held in state. Presentation matches ConfirmDialog:
          Cancel on the left, the destructive verb on the right, right-aligned.
          It was the reverse, and centred. */}
      <Modal open={!!deleteTarget} onClose={() => { if (!deleting) setDeleteTarget(null); }}
             title={deleteTarget ? `Delete admin ${deleteTarget.username}?` : ''}>
        <p>Their Cognito account is removed and they can no longer sign in.</p>
        <p className="text-muted">Your own account and the last superadmin cannot be deleted.</p>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" disabled={deleting} onClick={() => setDeleteTarget(null)}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" disabled={deleting} onClick={handleDelete}>
            {deleting ? <><span className="spinner-sm" /> Deleting…</> : 'Delete admin'}
          </button>
        </div>
      </Modal>

      {confirmDialog}
    </div>
  );
}

export default Admins;
